import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
    FaBars,
    FaTimes,
    FaChevronDown,
    FaSearch,
    FaGlobe,
    FaComments,
    FaAngleRight,

} from 'react-icons/fa';
import logo from '../assets/logo.jpeg';

const Navbar = () => {
    const [scrolled, setScrolled] = useState(false);
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const [activeDropdown, setActiveDropdown] = useState(null);
    const [mobileActiveSubmenu, setMobileActiveSubmenu] = useState(null);
    const location = useLocation();

    // Scroll Effect
    useEffect(() => {
        const handleScroll = () => setScrolled(window.scrollY > 20);
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    // Close mobile menu on route change
    useEffect(() => {
        setMobileMenuOpen(false);
        setMobileActiveSubmenu(null);
    }, [location]);

    // Menu Data Structure - Updated as per requirements
    const menuItems = [
        {
            title: "What We Do",
            hasMegaMenu: true,
            columns: [
                {
                    heading: "Digital Business",
                    links: [
                        { name: "Web Development", path: "/services/web-development" },
                        { name: "App Development", path: "/services/app-development" },
                        { name: "Software Development", path: "/services/software-development" },
                        { name: "Data Analytics", path: "/services/data-analytics" }
                    ]
                },
                {
                    heading: "Digital Marketing",
                    links: [
                        { name: "SEO Services", path: "/services/seo-services" },
                        { name: "Social Media Marketing", path: "/services/social-media-marketing" },
                        { name: "Content Marketing", path: "/services/content-marketing" },
                        { name: "PPC Advertising", path: "/services/ppc-advertising" }
                    ]
                },
                {
                    heading: "AI & Automation",
                    links: [
                        { name: "AI Solutions", path: "/services/ai-solutions" },
                        { name: "Process Automation", path: "/services/process-automation" },
                        { name: "Machine Learning", path: "/services/machine-learning" },
                        { name: "Chatbot Development", path: "/services/chatbot-development" }
                    ]
                }
            ]
        },
        {
            title: "Industries",
            hasMegaMenu: true,
            columns: [
                {
                    heading: "Financial Services",
                    links: [
                        { name: "Banking", path: "/industries/banking" },
                        { name: "Capital Markets", path: "/industries/capital-markets" },
                        { name: "Insurance", path: "/industries/insurance" }
                    ]
                },
                {
                    heading: "Manufacturing",
                    links: [
                        { name: "Automotive", path: "/industries/automotive" },
                        { name: "Industrial", path: "/industries/industrial" },
                        { name: "High-Tech", path: "/industries/high-tech" }
                    ]
                },
                {
                    heading: "Healthcare & Life Sciences",
                    links: [
                        { name: "Medical Devices", path: "/industries/medical-devices" },
                        { name: "Pharma", path: "/industries/pharma" },
                        { name: "Payer & Provider", path: "/industries/payer-provider" }
                    ]
                }
            ]
        },
        {
            title: "Products",
            hasMegaMenu: true,
            columns: [
                {
                    heading: "Business Software",
                    links: [
                        { name: "Expense Management Software", path: "/products/expense-management" },
                        { name: "Bulk WhatsApp SMS Software", path: "/products/bulk-whatsapp" }
                    ]
                }
            ]
        },
        {
            title: "About",
            hasMegaMenu: false,
            path: "/about"
        },
        {
            title: "Resources",
            hasMegaMenu: true,
            columns: [
                {
                    heading: "Insights",
                    links: [
                        { name: "Blog", path: "/blog" }
                    ]
                }
            ]
        },
        {
            title: "Careers",
            hasMegaMenu: false,
            path: "/careers"
        }
    ];

    // Mega Menu Hover Handlers (Desktop)
    const handleMouseEnter = (index) => setActiveDropdown(index);
    const handleMouseLeave = () => setActiveDropdown(null);

    return (
        <nav
            className={`fixed w-full z-50 transition-all duration-300 font-sans border-b border-gray-200 ${scrolled ? 'bg-white/95 backdrop-blur-md py-1 shadow-lg' : 'bg-white py-2'
                }`}
        >
            <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8">
                <div className="flex justify-between items-center h-14">

                    {/* LEFT: Logo & Tagline */}
                    <div className="flex items-center space-x-3">
                        <Link to="/" className="flex items-center space-x-2 group">
                            <img src={logo} alt="Logo" className="h-10" />
                        </Link>
                        <div className="hidden lg:block h-6 w-[1px] bg-gray-300"></div>

                    </div>

                    {/* CENTER: Desktop Mega Menu */}
                    <div className="hidden lg:flex items-center space-x-1">
                        {menuItems.map((item, index) => (
                            item.hasMegaMenu ? (
                                <div
                                    key={index}
                                    className="relative group px-2 py-1"
                                    onMouseEnter={() => handleMouseEnter(index)}
                                    onMouseLeave={handleMouseLeave}
                                >
                                    <button className="flex items-center space-x-1 text-dark/80 font-bold text-[14px] hover:text-primary transition-colors group-hover:text-primary uppercase tracking-wide">
                                        <span>{item.title}</span>
                                        <FaChevronDown className={`text-[10px] text-slate transition-transform duration-300 ${activeDropdown === index ? 'rotate-180 text-primary' : ''}`} />
                                    </button>

                                    {/* Mega Menu */}
                                    <AnimatePresence>
                                        {activeDropdown === index && (
                                            <motion.div
                                                initial={{ opacity: 0, y: 10 }}
                                                animate={{ opacity: 1, y: 0 }}
                                                exit={{ opacity: 0, y: 10 }}
                                                transition={{ duration: 0.2 }}
                                                className={`absolute left-1/2 transform -translate-x-1/2 top-full mt-1 bg-white border border-gray-100 shadow-2xl rounded-xl overflow-hidden w-[600px] p-6 grid grid-cols-${item.columns.length} gap-6`}
                                            >
                                                {/* Decoration */}
                                                <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-primary to-secondary"></div>

                                                {item.columns.map((col, colIndex) => (
                                                    <div key={colIndex} className="pt-2">
                                                        <h3 className="text-primary font-bold text-xs uppercase tracking-wider mb-3 border-b border-gray-100 pb-1">
                                                            {col.heading}
                                                        </h3>
                                                        <ul className="space-y-1.5">
                                                            {col.links.map((link, linkIndex) => (
                                                                <li key={linkIndex}>
                                                                    <Link to={link.path} className="flex items-center text-slate hover:text-secondary text-[13px] transition-colors group/link font-medium">
                                                                        <FaAngleRight className="opacity-0 -ml-3 mr-1 text-secondary text-[10px] transition-all group-hover/link:opacity-100 group-hover/link:ml-0" />
                                                                        {link.name}
                                                                    </Link>
                                                                </li>
                                                            ))}
                                                        </ul>
                                                    </div>
                                                ))}
                                            </motion.div>
                                        )}
                                    </AnimatePresence>
                                </div>
                            ) : (
                                <Link
                                    key={index}
                                    to={item.path}
                                    className="px-4 py-2 text-dark/80 font-bold text-[14px] hover:text-primary transition-colors uppercase tracking-wide"
                                >
                                    {item.title}
                                </Link>
                            )
                        ))}
                    </div>

                    {/* RIGHT: Utility Icons */}
                    <div className="hidden lg:flex items-center space-x-5 text-sm font-medium">
                        <button className="text-slate hover:text-primary transition-colors">
                            <FaSearch className="text-base" />
                        </button>

                        <div className="flex items-center space-x-1 text-slate hover:text-primary cursor-pointer transition-colors group">
                            <FaGlobe className="text-base group-hover:text-primary" />
                            <span className="font-bold">En</span>
                            <FaChevronDown className="text-[10px] group-hover:text-primary" />
                        </div>

                        <Link to="/contact" className="bg-primary text-white py-2 px-5 rounded-full hover:bg-secondary transition-colors shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 text-xs font-bold uppercase tracking-wide flex items-center gap-2">
                            <span>Get Quote</span>
                            <FaComments className="text-sm" />
                        </Link>
                    </div>

                    {/* Mobile Menu Toggle */}
                    <div className="lg:hidden flex items-center space-x-4">
                        <button className="text-slate hover:text-primary">
                            <FaSearch className="text-lg" />
                        </button>
                        <button
                            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                            className="text-dark text-xl focus:outline-none"
                        >
                            {mobileMenuOpen ? <FaTimes /> : <FaBars />}
                        </button>
                    </div>
                </div>
            </div>

            {/* MOBILE NAVIGATION OVERLAY */}
            <AnimatePresence>
                {mobileMenuOpen && (
                    <motion.div
                        initial={{ opacity: 0, x: '100%' }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: '100%' }}
                        transition={{ type: "tween", duration: 0.3 }}
                        className="fixed inset-0 top-[56px] bg-white z-40 overflow-y-auto pb-20 shadow-2xl border-t border-gray-100"
                    >
                        <div className="px-4 py-6 space-y-4">
                            {menuItems.map((item, index) => (
                                item.hasMegaMenu ? (
                                    <div key={index} className="border-b border-gray-100 last:border-0 pb-4">
                                        <button
                                            onClick={() => setMobileActiveSubmenu(mobileActiveSubmenu === index ? null : index)}
                                            className="flex justify-between items-center w-full text-left text-base font-bold text-dark py-2"
                                        >
                                            {item.title}
                                            <FaChevronDown className={`text-xs transition-transform ${mobileActiveSubmenu === index ? 'rotate-180 text-primary' : 'text-slate'}`} />
                                        </button>

                                        <AnimatePresence>
                                            {mobileActiveSubmenu === index && (
                                                <motion.div
                                                    initial={{ height: 0, opacity: 0 }}
                                                    animate={{ height: 'auto', opacity: 1 }}
                                                    exit={{ height: 0, opacity: 0 }}
                                                    className="overflow-hidden bg-gray-50 rounded-lg mt-2"
                                                >
                                                    <div className="p-4 space-y-6">
                                                        {item.columns.map((col, colIdx) => (
                                                            <div key={colIdx} className="space-y-3">
                                                                <h4 className="text-primary text-xs uppercase font-bold tracking-wider">{col.heading}</h4>
                                                                <ul className="space-y-2 border-l-2 border-gray-200 pl-4">
                                                                    {col.links.map((link, linkIdx) => (
                                                                        <li key={linkIdx}>
                                                                            <Link to={link.path} className="block text-slate text-sm hover:text-primary font-medium py-2">{link.name}</Link>
                                                                        </li>
                                                                    ))}
                                                                </ul>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </motion.div>
                                            )}
                                        </AnimatePresence>
                                    </div>
                                ) : (
                                    <div key={index} className="border-b border-gray-100 last:border-0 pb-4">
                                        <Link
                                            to={item.path}
                                            className="block text-base font-bold text-dark py-2 hover:text-primary transition-colors"
                                        >
                                            {item.title}
                                        </Link>
                                    </div>
                                )
                            ))}

                            <div className="pt-6 border-t border-gray-100 flex items-center justify-between">
                                <button className="flex items-center space-x-2 text-slate hover:text-primary font-bold">
                                    <FaGlobe />
                                    <span>Global (English)</span>
                                </button>
                                <Link to="/contact" className="px-6 py-2 bg-primary text-white font-bold rounded-lg text-sm shadow-md hover:bg-secondary">
                                    Contact Us
                                </Link>
                            </div>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </nav>
    );
};
export default Navbar;
